/**
 * A case's timeline — FR-E12, T-2.12. Split out of `CasePanels.tsx` in Wave
 * 15C; the behaviour is unchanged.
 *
 * **The filter chips come from the server.** `meta.known` is whatever is
 * registered in `app/domain/timeline.py`, so the documents and chat that Wave
 * 4B registers appear as chips without a web release — and `meta.sources` is
 * what actually *answered*, so a source having a bad afternoon renders as a
 * sentence rather than as a silent hole.
 *
 * The stage history (FR-E3) sits under it: the same case's moves, with the
 * reason each was given and whether the client saw it.
 */

import { Alert, Button, EmptyState, formatDateTime } from '@fiveox/ui'
import { History, ListTree } from 'lucide-react'
import { useEffect, useState } from 'react'

import { RecordSection } from '../../components/page'
import { type HistoryEntry, type TimelineEntry, caseFlows, historyLine } from '../../lib/api/cases'

export const KIND_LABELS: Record<string, string> = {
  history: 'Stage moves',
  task: 'Tasks',
  review: 'Reviews',
  callback: 'Call-backs',
  document: 'Documents',
  chat: 'Chat',
  invoice: 'Invoices',
}

function Entry({
  title,
  at,
  lines,
  detail,
}: {
  title: string
  at: string | null
  lines: string[]
  detail?: string | null
}) {
  return (
    <li className="border-border relative border-l pb-4 pl-5 last:pb-0">
      <span aria-hidden className="bg-primary absolute top-1.5 -left-[5px] h-2.5 w-2.5 rounded-full" />
      <p className="text-foreground text-sm font-medium">{title}</p>
      <p className="text-muted-foreground text-xs">
        {[at ? formatDateTime(at) : null, ...lines].filter(Boolean).join(' · ')}
      </p>
      {detail && <p className="text-muted-foreground mt-1 text-sm">{detail}</p>}
    </li>
  )
}

export function CaseTimeline({ caseId, history }: { caseId: string; history: HistoryEntry[] }) {
  const [entries, setEntries] = useState<TimelineEntry[]>([])
  const [known, setKnown] = useState<string[]>([])
  const [answered, setAnswered] = useState<string[]>([])
  const [filter, setFilter] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void caseFlows
      .timeline(caseId, filter ? [filter] : undefined)
      .then((body) => {
        setEntries(body.data)
        setKnown((body.meta?.known as string[]) ?? [])
        setAnswered((body.meta?.sources as string[]) ?? [])
      })
      .catch(() => setError('The timeline could not be loaded.'))
  }, [caseId, filter])

  // What was asked for and did not answer. Named, because a timeline with a
  // silent hole in it is worse than one that says which part is missing.
  const missing = (filter ? [filter] : known).filter((kind) => !answered.includes(kind))

  return (
    <div className="space-y-6">
      <RecordSection
        id="case-timeline"
        icon={<ListTree size={18} aria-hidden />}
        title="Timeline"
        description="Everything that happened on this case, newest first."
      >
        <div className="space-y-4">
          {error && <Alert tone="error">{error}</Alert>}
          {missing.length > 0 && (
            <Alert tone="warning">
              {missing.map((kind) => KIND_LABELS[kind] ?? kind).join(', ')} could not be loaded,
              so this timeline is incomplete.
            </Alert>
          )}

          <div role="group" aria-label="Show" className="flex flex-wrap gap-2">
            {[null, ...known].map((kind) => (
              <Button
                key={kind ?? 'all'}
                size="sm"
                variant={filter === kind ? 'primary' : 'outline'}
                aria-pressed={filter === kind}
                onClick={() => setFilter(kind)}
              >
                {kind === null ? 'Everything' : (KIND_LABELS[kind] ?? kind)}
              </Button>
            ))}
          </div>

          {entries.length === 0 ? (
            <EmptyState
              variant="outline"
              title="Nothing here yet"
              description="Stage moves, tasks, documents and messages on this case appear here as they happen."
            />
          ) : (
            <ol>
              {entries.map((entry) => (
                <Entry
                  key={`${entry.kind}:${entry.id}`}
                  title={entry.title}
                  at={entry.at}
                  lines={[
                    KIND_LABELS[entry.kind] ?? entry.kind,
                    entry.actor_kind === 'system' ? 'by ProDesk' : '',
                  ]}
                  detail={entry.detail}
                />
              ))}
            </ol>
          )}
        </div>
      </RecordSection>

      <RecordSection
        id="case-history"
        icon={<History size={18} aria-hidden />}
        title="Stage history"
        description="Every move, the reason it was given, and whether your client saw it."
      >
        {history.length === 0 ? (
          <p className="text-muted-foreground text-sm">The case has not moved yet.</p>
        ) : (
          <ol>
            {history.map((entry) => (
              <Entry
                key={entry.id}
                title={historyLine(entry)}
                at={entry.at}
                lines={[
                  entry.actor_kind === 'system' ? 'by ProDesk' : '',
                  entry.client_visible ? 'the client saw this' : '',
                ]}
                detail={entry.reason}
              />
            ))}
          </ol>
        )}
      </RecordSection>
    </div>
  )
}
